import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PlusCircle, Send, CheckCircle, Loader2, FileText,
  TrendingUp, ArrowRight, RefreshCw, AlertCircle,
  Upload, Info, Paperclip, X, Cpu, Zap, Brain, Download,
  Briefcase, MessageCircle, AlertTriangle,
} from 'lucide-react';
import api, { errorText } from '../config';
import { findingValue } from '../components/DigestView';

const PIPELINE_STEPS = [
  { key: 'financial_overview', label: 'Financial Screening',        icon: TrendingUp, desc: 'Computing EBITDA and implied multiples from submitted figures' },
  { key: 'memo',                label: 'Preliminary Memo Generation', icon: FileText,   desc: 'Drafting a screening summary from the numbers you provided' },
];

const DILIGENCE_DOMAINS = [
  { key: 'financials', label: 'Financial Statements',      accept: '.xlsx,.xls,.csv,.pdf', icon: FileText,  desc: 'Income statement, balance sheet, or P&L' },
  { key: 'hr',         label: 'HR / Org Chart',              accept: '.xlsx,.xls,.csv,.pdf', icon: Upload,    desc: 'Org chart or HRIS export with employee data' },
  { key: 'billing',    label: 'Billing / AR Data',          accept: '.xlsx,.xls,.csv,.pdf', icon: Upload,    desc: 'Invoice or AR aging export' },
  { key: 'crm',        label: 'Sales Pipeline / CRM',       accept: '.xlsx,.xls,.csv,.pdf', icon: Upload,    desc: 'CRM pipeline export with deal stages' },
];

const STATUS_META = {
  pending:          { label: 'Ready for upload', color: 'slate',   icon: Upload },
  uploading:        { label: 'Uploading...',     color: 'brand',   icon: Loader2 },
  uploaded:         { label: 'Caply is reading…', color: 'amber',  icon: Cpu },
  processed:        { label: 'Analysis Complete', color: 'emerald', icon: CheckCircle },
  error:            { label: 'Error',           color: 'red',     icon: AlertCircle },
  agent_unavailable:{ label: 'Temporarily unavailable', color: 'amber', icon: AlertCircle },
};

export default function Intake() {
  const [phase, setPhase] = useState('form'); // 'form' | 'pipeline' | 'result'
  const [formData, setFormData] = useState({
    companyName: '', sector: 'Enterprise SaaS', stage: '', targetValuation: '',
    annualRevenue: '', ebitdaMargin: '', leadPartner: 'Sarah Chen', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [dealResult, setDealResult] = useState(null);
  const [dupCheck, setDupCheck] = useState(null); // {matches, payload} duplicate guard
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertSuccess, setConvertSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Upload + diligence status state
  const [uploadStates, setUploadStates] = useState({}); // { [domain]: { loading, error, filename, size } }
  const [diligenceStatus, setDiligenceStatus] = useState(null); // response from /diligence-status
  const [pollingActive, setPollingActive] = useState(false);

  const timersRef = useRef([]);
  const pollIntervalRef = useRef(null);

  const clearTimers = () => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => () => {
    clearTimers();
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Poll diligence-status endpoint ──
  const pollDiligenceStatus = useCallback(async (dealId) => {
    if (!dealId) return;
    try {
      const resp = await api.get(`/api/intake/deals/${dealId}/diligence-status`).catch(() => null);
      if (resp && resp.data) {
        setDiligenceStatus(resp.data);
        // Stop polling when all REQUIRED domains are processed or errored
        // (stage-aware: optional domains don't block completion).
        const { summary } = resp.data;
        const requiredDone = summary && (
          summary.required_total > 0
            ? summary.required_processed + (summary.errors || 0) >= summary.required_total
            : summary.processed + summary.errors === summary.total
        );
        if (requiredDone) {
          setPollingActive(false);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }
    } catch {
      // silent fail — will retry next interval
    }
  }, []);

  const startPolling = useCallback((dealId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setPollingActive(true);
    pollDiligenceStatus(dealId); // immediate first poll
    pollIntervalRef.current = setInterval(() => pollDiligenceStatus(dealId), 3000);
  }, [pollDiligenceStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Empty strings break pydantic Optional[float] validation (422) — send null instead.
      const payload = { ...formData, stage: formData.stage || null };
      ['targetValuation', 'annualRevenue', 'ebitdaMargin', 'notes'].forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });

      // Duplicate guard: an existing deal with this name usually means the user
      // wants to UPDATE it, not split their work across two entries.
      try {
        const lookup = await api.get('/api/intake/deals/lookup', { params: { company: payload.companyName } });
        const matches = (lookup.data?.matches || []);
        if (matches.length > 0) {
          setDupCheck({ matches, payload });
          setSubmitting(false);
          return;
        }
      } catch { /* best-effort guard — never block intake on it */ }

      let submitErr = null;
      const response = await api.post('/api/intake/deals', payload).catch((err) => {
        submitErr = err;
        return null;
      });

      if (!response || !response.data) {
        const detail = submitErr?.response?.data?.detail;
        setError(
          detail
            ? `Deal rejected: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
            : 'Failed to submit deal. API unreachable.'
        );
        setSubmitting(false);
        return;
      }

      const result = response.data;
      setDealResult(result);
      setPhase('pipeline');
      setSubmitting(false);
      // Kick off diligence-status polling right away so Required/Optional
      // badges and the stage rationale render before the first upload.
      startPolling(result.id);

      let delay = 500;
      PIPELINE_STEPS.forEach((step, idx) => {
        timersRef.current.push(setTimeout(() => setActiveStep(idx), delay));
        delay += 900;
        timersRef.current.push(
          setTimeout(() => {
            setCompletedSteps(prev => [...prev, step.key]);
            setActiveStep(-1);
          }, delay)
        );
        delay += 200;
      });

      timersRef.current.push(setTimeout(() => setPhase('result'), delay + 300));
    } catch {
      setError('Failed to submit deal proposal. Please check API status.');
      setSubmitting(false);
    }
  };

  const handleConvertToPortco = async (formData) => {
    if (!dealResult?.id) return;
    try {
      const resp = await api.post(`/api/portfolio/deals/${dealResult.id}/convert`, formData);
      setConvertSuccess(resp.data);
      setShowConvertForm(false);
    } catch (err) {
      console.error('Conversion failed:', err);
    }
  };

  const handleReset = () => {
    clearTimers();
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
    setPhase('form');
    setActiveStep(-1);
    setCompletedSteps([]);
    setDealResult(null);
    setError(null);
    setUploadStates({});
    setDiligenceStatus(null);
    setPollingActive(false);
    setFormData({
      companyName: '', sector: 'Enterprise SaaS', stage: '', targetValuation: '',
      annualRevenue: '', ebitdaMargin: '', leadPartner: 'Sarah Chen', notes: ''
    });
  };

  // ── Download / delete an uploaded diligence file (deal-scoped) ──
  const handleFileDownload = async (domain, fileIndex, filename) => {
    if (!dealResult?.id) return;
    try {
      const resp = await api.get(
        `/api/intake/deals/${dealResult.id}/uploads/${domain}/${fileIndex}/download`,
        { responseType: 'blob', timeout: 60000 }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      setUploadStates(prev => ({ ...prev, _dl_error: 'Download failed — file may no longer exist.' }));
    }
  };

  const handleFileDelete = async (domain, fileIndex) => {
    if (!dealResult?.id) return;
    try {
      await api.delete(`/api/intake/deals/${dealResult.id}/uploads/${domain}/${fileIndex}`);
      // Drop the deleted file from the local session state so the list
      // updates immediately, then re-poll to sync pending counts.
      setUploadStates(prev => {
        const st = { ...prev };
        const u = st[domain];
        if (u?.uploadedFiles) {
          st[domain] = { ...u, uploadedFiles: u.uploadedFiles.filter((_, i) => i !== fileIndex) };
        }
        return st;
      });
      pollDiligenceStatus(dealResult.id);
    } catch {
      setUploadStates(prev => ({ ...prev, _del_error: 'Delete failed — try refreshing.' }));
    }
  };

  const handleFileUpload = async (domain, file) => {
    if (!dealResult?.id) return;
    setUploadStates(prev => ({ ...prev, [domain]: { loading: true } }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('domain', domain);

    try {
      const response = await api.post(
        `/api/intake/deals/${dealResult.id}/upload`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      ).catch((err) => ({ error: err }));

      if (response.error || !response.data) {
        setUploadStates(prev => ({
          ...prev,
          [domain]: { loading: false, error: errorText(response.error, 'Upload failed') }
        }));
        return;
      }

      setUploadStates(prev => ({
        ...prev,
        [domain]: {
          loading: false,
          success: true,
          filename: response.data.filename,
          size: response.data.size_bytes,
          agentStatus: response.data.agent_status,
          agentResult: response.data.agent_result,
        }
      }));

      // Start polling if not already active
      if (!pollingActive && !pollIntervalRef.current) {
        startPolling(dealResult.id);
      } else {
        // Already polling — do a fresh poll right away
        pollDiligenceStatus(dealResult.id);
      }
    } catch (err) {
      setUploadStates(prev => ({
        ...prev,
        [domain]: { loading: false, error: 'Upload failed — check API status' }
      }));
    }
  };

  // ─── FORM PHASE ───
  // ── Duplicate guard actions ──
  const mergeIntoExisting = async (existing) => {
    if (!dupCheck) return;
    setSubmitting(true);
    setError(null);
    try {
      // Update the EXISTING deal with the details just typed (non-empty fields only)
      const { payload } = dupCheck;
      const patch = {};
      ['sector', 'stage', 'targetValuation', 'annualRevenue', 'ebitdaMargin', 'leadPartner', 'notes'].forEach((k) => {
        if (payload[k] !== null && payload[k] !== undefined && payload[k] !== '') patch[k] = payload[k];
      });
      await api.patch(`/api/intake/deals/${existing.id}`, patch);
      setDupCheck(null);
      setDealResult({
        id: existing.id,
        companyName: existing.companyName,
        status: 'active',
        message: `Updated the existing deal for ${existing.companyName} with your new details.`,
      });
      startPolling(existing.id);
      setPhase('pipeline');
    } catch (err) {
      setError(errorText(err, 'Could not update the existing deal.'));
    } finally {
      setSubmitting(false);
    }
  };

  const createAnyway = async () => {
    if (!dupCheck) return;
    const { payload } = dupCheck;
    setDupCheck(null);
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post('/api/intake/deals', payload);
      const result = response.data;
      setDealResult(result);
      setPhase('pipeline');
    } catch (err) {
      setError(errorText(err, 'Failed to submit deal.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'form') {
    return (
      <div className="page-container max-w-3xl mx-auto">
        <div>
          <h2 className="page-title">Add a New Deal</h2>
          <p className="page-subtitle">Enter a company’s details and Caply gives you an honest first read — based only on the figures you provide</p>
        </div>

        {error && (
          <div className="alert-error">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DealDocumentLibrary currentDealId={null} />

        {dupCheck && (
          <div className="card border-amber-300 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="text-sm text-amber-900">
                <span className="font-semibold">
                  A deal for &ldquo;{dupCheck.matches[0]?.companyName}&rdquo; already exists.
                </span>{' '}
                To keep all your work in one place, you can update it with the details you just entered
                instead of creating a second entry.
                <ul className="mt-2 space-y-1">
                  {dupCheck.matches.map((m) => (
                    <li key={m.id} className="text-xs text-amber-800">
                      • Created {new Date(m.created_at).toLocaleDateString()} ·{' '}
                      {m.has_financials ? 'has financial details' : 'no financial details yet'}
                      {m.deal_phase ? ` · phase: ${m.deal_phase}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pl-7">
              <button
                type="button"
                onClick={() => mergeIntoExisting(dupCheck.matches[0])}
                disabled={submitting}
                className="btn-primary text-xs py-1.5 px-3"
              >
                {submitting ? 'Updating…' : 'Update existing deal with these details'}
              </button>
              <button
                type="button"
                onClick={createAnyway}
                disabled={submitting}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                Create as a new deal anyway
              </button>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="card space-y-5">
          <div className="card-header">
            <h3 className="card-title">
              <PlusCircle className="w-5 h-5 text-brand-600" />
              Target Company
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Company Name</label>
              <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange} placeholder="e.g. Apex CyberSecurity" className="form-input" />
            </div>
            <div>
              <label className="form-label">Sector</label>
              <select name="sector" value={formData.sector} onChange={handleChange} className="form-select">
                <option>Enterprise SaaS</option>
                <option>HealthTech</option>
                <option>Industrial Automation</option>
                <option>CleanTech SaaS</option>
                <option>FinTech</option>
                <option>Consumer Products</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="form-label">Company Stage</label>
              <select name="stage" value={formData.stage} onChange={handleChange} className="form-select">
                <option value="">Not sure — infer from financials</option>
                <option value="early">Early — product-market fit, growth over profit</option>
                <option value="growth">Growth — scaling revenue, path to profitability</option>
                <option value="mature">Mature — stable, cash-generative business</option>
                <option value="pre_exit">Pre-Exit — harvest prep, positioning for sale</option>
              </select>
            </div>
            <div>
              <label className="form-label">Target Valuation ($)</label>
              <input type="number" name="targetValuation" value={formData.targetValuation} onChange={handleChange} placeholder="50000000" className="form-input" />
            </div>
            <div>
              <label className="form-label">Annual Revenue ($)</label>
              <input type="number" name="annualRevenue" value={formData.annualRevenue} onChange={handleChange} placeholder="20000000" className="form-input" />
            </div>
            <div>
              <label className="form-label">EBITDA Margin (%)</label>
              <input type="number" step="0.1" name="ebitdaMargin" value={formData.ebitdaMargin} onChange={handleChange} placeholder="25.0" className="form-input" />
            </div>
            <div>
              <label className="form-label">Lead Partner</label>
              <select name="leadPartner" value={formData.leadPartner} onChange={handleChange} className="form-select">
                <option>Sarah Chen</option>
                <option>Marcus Williams</option>
                <option>Elena Rodriguez</option>
                <option>James Park</option>
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">Deal Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} placeholder="Strategic rationale, synergies, competitive landscape..." className="form-input" />
          </div>

          <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500">
              This produces a preliminary screening using only the figures above. For full diligence (GL normalization, org structure, AR aging, sales pipeline), you can upload supporting documents after submission.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? 'Submitting...' : 'Submit & Screen'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── PIPELINE PHASE ───
  if (phase === 'pipeline') {
    return (
      <div className="page-container max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-50 mb-4">
            <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-navy-900">Screening {dealResult?.companyName}</h2>
          <p className="text-sm text-slate-500 mt-1">Computing figures and drafting a preliminary summary</p>
        </div>

        <div className="card">
          <div className="space-y-1">
            {PIPELINE_STEPS.map((step, idx) => {
              const isActive = activeStep === idx;
              const isComplete = completedSteps.includes(step.key);
              const StepIcon = step.icon;

              return (
                <div
                  key={step.key}
                  className={`flex items-start gap-4 p-4 rounded-lg transition-all duration-300 ${
                    isActive ? 'bg-brand-50 border border-brand-200' : isComplete ? '' : 'opacity-50'
                  }`}
                >
                  <div className="shrink-0">
                    {isComplete ? (
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                    ) : isActive ? (
                      <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-brand-600 animate-spin" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <StepIcon className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold ${
                        isComplete ? 'text-emerald-700' : isActive ? 'text-brand-700' : 'text-slate-500'
                      }`}>
                        {step.label}
                      </p>
                      {isComplete && <span className="text-xs text-emerald-500 font-medium">✓ Done</span>}
                      {isActive && <span className="text-xs text-brand-500 font-medium animate-pulse">Working...</span>}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{step.desc}</p>
                  </div>
                </div>
              );
            })}

            {/* Honest note about what's NOT running */}
            <div className="flex items-start gap-4 p-4 rounded-lg opacity-60 border border-dashed border-slate-200 mt-2">
              <div className="shrink-0">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-slate-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-500">GL / HR / Billing / Sales Analysis</p>
                <p className="text-xs text-slate-400 mt-0.5">Not run — upload documents after screening to unlock these analyses</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULT PHASE ───
  const agentResults = dealResult?.agent_results || {};
  const financialOverview = agentResults.financial_overview || {};
  const memoText = agentResults.memo || '';
  const diligencePending = agentResults.diligence_pending || [];
  const hadPartialError = agentResults.status === 'partial';

  const fmtMoney = (v) => (typeof v === 'number' ? `$${(v / 1e6).toFixed(1)}M` : '—');
  const fmtPct = (v) => (typeof v === 'number' ? `${v.toFixed(1)}%` : '—');
  const fmtMultiple = (v) => (typeof v === 'number' ? `${v.toFixed(1)}x` : '—');
  const fmtFileSize = (bytes) => bytes > 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${(bytes / 1e3).toFixed(0)} KB`;

  // Merge poll data with local upload state for display
  const domainStatusMap = diligenceStatus?.domains || {};
  const summary = diligenceStatus?.summary || null;

  return (
    <div className="page-container max-w-5xl mx-auto">
      {/* Status banner */}
      <div className="flex items-center gap-4 p-5 rounded-xl bg-emerald-50 border border-emerald-200">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <CheckCircle className="w-6 h-6 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-emerald-800">Preliminary Screening Complete — {dealResult?.companyName}</h2>
          <p className="text-sm text-emerald-600 mt-0.5">Deal ID: {dealResult?.id} · Based solely on submitted figures</p>
        </div>
        {hadPartialError && <span className="badge badge-amber text-xs">Partial</span>}
      </div>

      {/* Real KPI tiles — only what was actually submitted/computed */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {financialOverview.annual_revenue != null && (
          <div className="kpi-card">
            <div className="absolute top-0 left-0 w-1 h-full bg-brand-600/80" />
            <p className="kpi-label">Annual Revenue</p>
            <p className="kpi-value">{fmtMoney(financialOverview.annual_revenue)}</p>
            <p className="text-xs text-slate-400 mt-1">As submitted</p>
          </div>
        )}
        {financialOverview.estimated_ebitda != null && (
          <div className="kpi-card">
            <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500/80" />
            <p className="kpi-label">Estimated EBITDA</p>
            <p className="kpi-value">{fmtMoney(financialOverview.estimated_ebitda)}</p>
            <p className="text-xs text-slate-400 mt-1">
              {fmtPct(financialOverview.ebitda_margin_pct)} margin (computed)
            </p>
          </div>
        )}
        {financialOverview.target_valuation != null && (
          <div className="kpi-card">
            <div className="absolute top-0 left-0 w-1 h-full bg-purple-500/80" />
            <p className="kpi-label">Target Valuation</p>
            <p className="kpi-value">{fmtMoney(financialOverview.target_valuation)}</p>
            <p className="text-xs text-slate-400 mt-1">As submitted</p>
          </div>
        )}
        {financialOverview.implied_ebitda_multiple != null && (
          <div className="kpi-card">
            <div className="absolute top-0 left-0 w-1 h-full bg-amber-500/80" />
            <p className="kpi-label">Implied EBITDA Multiple</p>
            <p className="kpi-value">{fmtMultiple(financialOverview.implied_ebitda_multiple)}</p>
            <p className="text-xs text-slate-400 mt-1">
              Rev multiple: {fmtMultiple(financialOverview.implied_revenue_multiple)}
            </p>
          </div>
        )}
      </div>

      {/* Screening memo */}
      {memoText && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <FileText className="w-5 h-5 text-brand-600" />
              Preliminary Screening Summary
            </h3>
            <span className="badge badge-slate">Based on submitted figures</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{memoText}</p>
        </div>
      )}

      {/* ── Diligence Pipeline with Real-Time Agent Status ── */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <Cpu className="w-5 h-5 text-brand-600" />
            Agent Diligence Pipeline
          </h3>
          {pollingActive && (
            <span className="inline-flex items-center gap-1.5 text-xs text-brand-600 font-medium">
              <Loader2 className="w-3 h-3 animate-spin" />
              Polling live...
            </span>
          )}
        </div>

        {/* Pipeline progress bar */}
        {summary && (
          <div className="mb-5">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-medium text-slate-600">
                {summary.processed} of {summary.total} domains processed
              </span>
              <span className="text-slate-400">
                {summary.pending} pending · {summary.errors} errors
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${(summary.processed / summary.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        <p className="text-sm text-slate-500 mb-4">
          Upload supporting documents to unlock the full analysis. Caply reads each file and its findings appear below as they’re ready.
        </p>

        <div className="space-y-3">
          {DILIGENCE_DOMAINS.map((domain) => {
            const uploadState = uploadStates[domain.key] || {};
            const pollDomain = domainStatusMap[domain.key] || {};
            const DomainIcon = domain.icon;

            // Determine the effective status
            // Priority: upload loading > poll status > upload state
            let effectiveStatus = 'pending';
            if (uploadState.loading) {
              effectiveStatus = 'uploading';
            } else if (pollDomain.status) {
              effectiveStatus = pollDomain.status;
            } else if (uploadState.success) {
              effectiveStatus = 'uploaded';
            } else if (uploadState.error) {
              effectiveStatus = 'error';
            }

            const meta = STATUS_META[effectiveStatus] || STATUS_META.pending;
            const StatusIcon = meta.icon;

            // Get agent result from poll or upload response
            const agentResult = pollDomain.agent_result || uploadState.agentResult || null;
            const files = uploadState.uploadedFiles || pollDomain.files || (uploadState.filename ? [{
              filename: uploadState.filename,
              size_bytes: uploadState.size,
              uploaded_at: new Date().toISOString(),
            }] : []);

            return (
              <div
                key={domain.key}
                className={`border rounded-lg p-4 transition-all ${
                  effectiveStatus === 'processed' ? 'border-emerald-200 bg-emerald-50/30' :
                  effectiveStatus === 'uploaded' ? 'border-amber-200 bg-amber-50/30' :
                  effectiveStatus === 'error' || effectiveStatus === 'agent_unavailable' ? 'border-red-200 bg-red-50/30' :
                  effectiveStatus === 'uploading' ? 'border-brand-300 bg-brand-50/30' :
                  'border-slate-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Status icon */}
                  <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    effectiveStatus === 'processed' ? 'bg-emerald-100' :
                    effectiveStatus === 'uploaded' ? 'bg-amber-100' :
                    effectiveStatus === 'error' || effectiveStatus === 'agent_unavailable' ? 'bg-red-100' :
                    effectiveStatus === 'uploading' ? 'bg-brand-100' :
                    'bg-slate-100'
                  }`}>
                    <StatusIcon className={`w-5 h-5 ${
                      effectiveStatus === 'processed' ? 'text-emerald-600' :
                      effectiveStatus === 'uploaded' ? 'text-amber-600' :
                      effectiveStatus === 'error' || effectiveStatus === 'agent_unavailable' ? 'text-red-500' :
                      effectiveStatus === 'uploading' ? 'text-brand-600 animate-spin' :
                      'text-slate-400'
                    } ${effectiveStatus === 'uploading' ? 'animate-spin' : ''}`} />
                  </div>

                  {/* Domain info + upload */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-navy-900">{domain.label}</p>
                      {(pollDomain.required === true) && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-navy-900 text-white">Required</span>
                      )}
                      {(pollDomain.required === false) && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Optional</span>
                      )}
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        meta.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                        meta.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                        meta.color === 'red' ? 'bg-red-100 text-red-600' :
                        meta.color === 'brand' ? 'bg-brand-100 text-brand-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-xs text-slate-400">{domain.desc}</p>

                    </div>

                    {/* Uploaded file info with download & delete */}
                    {files.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {files.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <Paperclip className="w-3 h-3 text-emerald-500 shrink-0" />
                            <span className="truncate max-w-[200px] text-slate-700">{f.filename}</span>
                            {f.size_bytes && <span className="text-slate-400">({fmtFileSize(f.size_bytes)})</span>}
                            <button
                              onClick={() => handleFileDownload(domain.key, f.file_index ?? i, f.filename)}
                              className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded px-1.5 py-0.5 transition-colors"
                              title="Download file"
                            >
                              <Download className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleFileDelete(domain.key, f.file_index ?? i)}
                              className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-1.5 py-0.5 transition-colors"
                              title="Delete file"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload error */}
                    {uploadState.error && (
                      <p className="mt-2 text-xs text-red-500">{uploadState.error}</p>
                    )}

                    {/* ── Agent Results ── */}
                    {agentResult && effectiveStatus === 'processed' && (
                      <div className="mt-3 bg-white border border-emerald-200 rounded-lg p-3 space-y-2">
                        {/* Summary line */}
                        {agentResult.summary && (
                          <div className="flex items-start gap-1.5">
                            <Zap className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-700">{agentResult.summary}</p>
                          </div>
                        )}

                        {/* Findings (structured) */}
                        {agentResult.findings && typeof agentResult.findings === 'object' && (
                          <div className="space-y-1">
                            {Object.entries(agentResult.findings).slice(0, 6).map(([k, v]) => (
                              <div key={k} className="flex items-start gap-2 text-xs">
                                <span className="font-medium text-slate-500 capitalize shrink-0">
                                  {k.replace(/_/g, ' ')}:
                                </span>
                                <span className="text-slate-700">{findingValue(v)}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Anomalies (if present) */}
                        {agentResult.findings?.anomalies_detected && Array.isArray(agentResult.findings.anomalies_detected) && agentResult.findings.anomalies_detected.length > 0 && (
                          <div className="mt-2 border-t border-slate-100 pt-2">
                            <p className="text-xs font-semibold text-red-600 mb-1">
                              ⚠ {agentResult.findings.anomalies_detected.length} anomalies detected
                            </p>
                            {agentResult.findings.anomalies_detected.slice(0, 3).map((a, i) => (
                              <p key={i} className="text-xs text-slate-600 ml-3">
                                • {a.description || a.issue || a.summary || findingValue(a)}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Agent error */}
                    {effectiveStatus === 'error' && agentResult?.error && (
                      <div className="mt-2 text-xs text-red-500 bg-red-50 border border-red-200 rounded p-2">
                        {agentResult.error}
                      </div>
                    )}

                    {/* Processing indicator */}
                    {effectiveStatus === 'uploaded' && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Caply is reading and analyzing your file…
                      </div>
                    )}

                    {/* Upload button */}
                    {effectiveStatus === 'pending' && !uploadState.loading && (
                      <label className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer rounded-md px-3 py-1.5 text-brand-600 hover:bg-brand-50 transition-colors">
                        <Upload className="w-3.5 h-3.5" />
                        Choose File
                        <input
                          type="file"
                          accept={domain.accept}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(domain.key, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}

                    {/* Replace button (when processed or uploaded) */}
                    {(effectiveStatus === 'processed' || effectiveStatus === 'uploaded' || effectiveStatus === 'agent_unavailable') && (
                      <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer rounded-md px-3 py-1.5 text-slate-500 hover:bg-slate-50 transition-colors">
                        <Upload className="w-3.5 h-3.5" />
                        Replace File
                        <input
                          type="file"
                          accept={domain.accept}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(domain.key, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stage profile note */}
        {diligenceStatus?.stage_label && diligenceStatus?.stage_profile && (
          <div className="mt-4 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600">
              <span className="font-semibold text-navy-900">{diligenceStatus.stage_label} stage:</span>{' '}
              {diligenceStatus.stage_profile.rationale}
            </p>
          </div>
        )}

        {/* Pipeline completion banner — stage-aware */}
        {summary && summary.required_total > 0 && summary.required_processed === summary.required_total && (
          <div className="mt-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-700">
              All {summary.required_total} required diligence domain{summary.required_total !== 1 ? 's' : ''} processed for a {diligenceStatus?.stage_label || ''}-stage deal. Optional domains still enrich the analysis.
            </p>
          </div>
        )}

        {summary && summary.processed > 0 && (summary.required_total === 0 || summary.required_processed < summary.required_total) && (
          <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <Loader2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5 animate-spin" />
            <p className="text-xs text-amber-700">
              {summary.required_processed} of {summary.required_total} required domains complete. Upload the remaining required document{(summary.required_total - summary.required_processed) !== 1 ? 's' : ''} to complete {diligenceStatus?.stage_label ? `${diligenceStatus.stage_label}-stage` : 'this'} diligence.
            </p>
          </div>
        )}
      </div>

      {/* Conversion success banner */}
      {convertSuccess && (
        <div className="flex items-center gap-4 p-5 rounded-xl bg-emerald-50 border border-emerald-200">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-emerald-800">Converted to Portfolio Company</h3>
            <p className="text-xs text-emerald-600 mt-0.5">
              {convertSuccess.portco_name} is now tracked in your portfolio.
            </p>
          </div>
          <button
            onClick={() => { window.location.href = `/portfolio/${convertSuccess.portco_id}`; }}
            className="btn-primary text-sm"
          >
            <Briefcase className="w-4 h-4" />
            View Portco
          </button>
        </div>
      )}

      {dealResult && <ConciergePanel dealId={dealResult.id} />}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button onClick={handleReset} className="btn-secondary">
          <RefreshCw className="w-4 h-4" />
          Submit Another Deal
        </button>
        <div className="flex items-center gap-3">
          {!convertSuccess && (
            <button
              onClick={() => setShowConvertForm(true)}
              className="btn-secondary"
              title="Convert this deal into a tracked portfolio company"
            >
              <Briefcase className="w-4 h-4" />
              Convert to Portco
            </button>
          )}
          <button onClick={() => { window.location.href = '/reports'; }} className="btn-primary">
            <FileText className="w-4 h-4" />
            Go to Reports
          </button>
        </div>
      </div>

      <DealDocumentLibrary currentDealId={dealResult?.id} />

      {/* Convert to Portco Modal */}
      {showConvertForm && (
        <ConvertModal
          dealId={dealResult?.id}
          companyName={dealResult?.companyName}
          onSave={handleConvertToPortco}
          onClose={() => setShowConvertForm(false)}
        />
      )}
    </div>
  );
}


// ── Deal Document Library ──────────────────────────────────────────────────
// Tenant-scoped: the server only ever returns deals and files that belong to
// the signed-in firm, so this panel can never surface another firm's data.
// It lists every document uploaded for a deal (grouped by diligence domain),
// across sessions — not just files uploaded during the current visit.
const LIB_DOMAIN_META = {
  financials: { label: 'Financial Statements', color: 'bg-brand-100 text-brand-700' },
  hr:         { label: 'HR / Org Chart',      color: 'bg-amber-100 text-amber-700' },
  billing:    { label: 'Billing / AR Data',   color: 'bg-emerald-100 text-emerald-700' },
  crm:        { label: 'Sales Pipeline / CRM', color: 'bg-red-100 text-red-600' },
};

function DealDocumentLibrary({ currentDealId }) {
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [uploads, setUploads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fmtSize = (bytes) =>
    bytes > 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${(bytes / 1e3).toFixed(0)} KB`;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return ''; }
  };

  // Load the firm's deals once (server filters by tenant)
  useEffect(() => {
    let alive = true;
    api.get('/api/intake/deals').then(r => {
      if (alive) setDeals(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Follow the deal created in this session, if any
  useEffect(() => {
    if (currentDealId) {
      setSelectedDealId(currentDealId);
      setDeals(prev => prev.some(d => d.id === currentDealId)
        ? prev
        : [{ id: currentDealId, companyName: 'Current deal' }, ...prev]);
    }
  }, [currentDealId]);

  const refresh = useCallback(async (dealId) => {
    if (!dealId) { setUploads(null); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/api/intake/deals/${dealId}/uploads`);
      setUploads(r.data);
    } catch {
      setUploads(null);
      setError('Could not load documents for this deal.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(selectedDealId); }, [selectedDealId, refresh]);

  const download = async (domain, rec) => {
    try {
      const resp = await api.get(
        `/api/intake/deals/${selectedDealId}/uploads/${domain}/${rec.file_index}/download`,
        { responseType: 'blob', timeout: 60000 }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = rec.filename || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      setError('Download failed — file may no longer exist.');
    }
  };

  const remove = async (domain, rec) => {
    try {
      await api.delete(`/api/intake/deals/${selectedDealId}/uploads/${domain}/${rec.file_index}`);
      refresh(selectedDealId);
    } catch {
      setError('Delete failed — try refreshing.');
    }
  };

  const dealName = deals.find(d => d.id === selectedDealId)?.companyName || 'this deal';
  const totalFiles = uploads
    ? Object.values(uploads.uploads || {}).reduce((n, recs) => n + recs.length, 0)
    : 0;

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <Paperclip className="w-5 h-5 text-brand-600" />
          Deal Document Library
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={selectedDealId || ''}
            onChange={(e) => setSelectedDealId(e.target.value)}
            className="form-select text-sm max-w-56"
          >
            <option value="">Choose a deal…</option>
            {deals.map(d => (
              <option key={d.id} value={d.id}>{d.companyName}</option>
            ))}
          </select>
          <button
            onClick={() => refresh(selectedDealId)}
            disabled={!selectedDealId || loading}
            className="p-2 text-slate-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg disabled:opacity-40 transition-colors"
            title="Refresh document list"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {!selectedDealId ? (
        <p className="text-sm text-slate-500 py-4">
          Pick one of your deals to see every document your firm has uploaded for it.
          Files stay listed across visits, and only your firm can ever see them.
        </p>
      ) : loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
        </div>
      ) : error && !uploads ? (
        <p className="text-sm text-red-500 py-4">{error}</p>
      ) : totalFiles === 0 ? (
        <p className="text-sm text-slate-500 py-4">
          No documents uploaded for <b>{dealName}</b> yet. Upload files in the diligence
          stage and they will appear here for your whole firm.
        </p>
      ) : (
        <div className="space-y-4 py-2">
          {Object.entries(uploads.uploads).map(([domain, recs]) => recs.length > 0 && (
            <div key={domain}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${LIB_DOMAIN_META[domain]?.color || 'bg-slate-100 text-slate-600'}`}>
                  {LIB_DOMAIN_META[domain]?.label || domain}
                </span>
                <span className="text-xs text-slate-400">{recs.length} file{recs.length > 1 ? 's' : ''}</span>
              </div>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {recs.map((rec, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2">
                    <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-sm text-slate-800">{rec.filename}</span>
                    <span className="text-xs text-slate-400 shrink-0">{fmtSize(rec.size_bytes || 0)}</span>
                    <span className="text-xs text-slate-400 shrink-0 hidden sm:inline">{fmtDate(rec.uploaded_at)}</span>
                    {rec.uploaded_by && (
                      <span className="text-xs text-slate-400 shrink-0 hidden md:inline">by {rec.uploaded_by}</span>
                    )}
                    <button
                      onClick={() => download(domain, rec)}
                      className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded px-1.5 py-1 transition-colors shrink-0"
                      title="Download file"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(domain, rec)}
                      className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-1.5 py-1 transition-colors shrink-0"
                      title="Delete file"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {error && <p className="text-xs text-red-500">{error}</p>}
          <p className="text-xs text-slate-400">
            {totalFiles} document{totalFiles > 1 ? 's' : ''} for <b>{dealName}</b> — visible only to your firm.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Convert Deal to Portco Modal ──
function ConvertModal({ dealId, companyName, onSave, onClose }) {
  const [form, setForm] = useState({
    portco_name: companyName || '',
    initial_investment: '',
    acquisition_date: new Date().toISOString().slice(0, 10),
    lead_partner: '',
    moic: '1.0',
    irr: '0',
    notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      portco_name: form.portco_name,
      initial_investment: parseFloat(form.initial_investment) || 0,
      acquisition_date: form.acquisition_date,
      lead_partner: form.lead_partner || null,
      moic: parseFloat(form.moic) || 1.0,
      irr: parseFloat(form.irr) || 0,
      notes: form.notes || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-navy-900">Convert to Portfolio Company</h3>
            <p className="text-xs text-slate-500 mt-0.5">Deal: {dealId}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="form-label">Company Name</label>
            <input
              className="form-input"
              required
              value={form.portco_name}
              onChange={e => setForm({...form, portco_name: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Initial Investment ($)</label>
              <input
                className="form-input"
                type="number"
                required
                value={form.initial_investment}
                onChange={e => setForm({...form, initial_investment: e.target.value})}
                placeholder="Deal size from intake"
              />
            </div>
            <div>
              <label className="form-label">Acquisition Date</label>
              <input
                className="form-input"
                type="date"
                required
                value={form.acquisition_date}
                onChange={e => setForm({...form, acquisition_date: e.target.value})}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Initial MOIC (x)</label>
              <input
                className="form-input"
                type="number"
                step="0.1"
                value={form.moic}
                onChange={e => setForm({...form, moic: e.target.value})}
              />
            </div>
            <div>
              <label className="form-label">Initial IRR (%)</label>
              <input
                className="form-input"
                type="number"
                step="0.1"
                value={form.irr}
                onChange={e => setForm({...form, irr: e.target.value})}
              />
            </div>
          </div>
          <div>
            <label className="form-label">Lead Partner</label>
            <input
              className="form-input"
              value={form.lead_partner}
              onChange={e => setForm({...form, lead_partner: e.target.value})}
              placeholder="Sarah Chen"
            />
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
            Company details (revenue, EBITDA, industry, geography) will be auto-pulled from the deal submission.
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1">
              <Briefcase className="w-4 h-4" />
              Convert & Register
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── AI Concierge: grounded Q&A over this deal ──
function ConciergePanel({ dealId }) {
  const [messages, setMessages] = useState([]);   // {role: 'user'|'assistant', text}
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);

  const suggestions = [
    "What's driving the EBITDA margin in this deal?",
    "What is still missing from diligence?",
    "What should I verify with management?",
  ];

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || busy) return;
    setQuestion('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const history = messages.slice(-4).map(m =>
        m.role === 'user' ? { question: m.text } : { answer: m.text }
      );
      const resp = await api.post('/api/agents/concierge/chat', {
        message: q,
        context: { deal_id: dealId, history },
      });
      setMessages(prev => [...prev, { role: 'assistant', text: resp.data.response || resp.data }]);
    } catch (err) {
      const detail = errorText(err, err.message);
      setMessages(prev => [...prev, { role: 'assistant', text: `Sorry, that didn't work — ${detail}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-brand-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center">
          <MessageCircle className="w-4 h-4" />
        </div>
        <h3 className="text-sm font-bold text-navy-900">Ask Caply AI</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Ask "what" and "why" questions about this deal — answers come only from the numbers
        and documents you've provided. Caply never invents figures, and it won't make the
        invest/don't-invest call for you.
      </p>

      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {suggestions.map(sg => (
            <button
              key={sg}
              onClick={() => ask(sg)}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-full border border-brand-200 text-brand-700 bg-brand-50 hover:bg-brand-100 transition disabled:opacity-50"
            >
              {sg}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-xl px-3.5 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-brand-600 text-navy-950'
                : 'bg-slate-100 text-slate-700 border border-slate-200'
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-xl px-3.5 py-2 bg-slate-100 border border-slate-200 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the deal data…
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="flex items-center gap-2"
      >
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask about this deal…"
          disabled={busy}
          className="flex-1 text-sm rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={busy || !question.trim()}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
